/**
 * OKLCH maths and the ramp derivation. Pure; `tokens.js` owns policy (families, character).
 */

/* ------------------------------------------------------------------ *
 * sRGB <-> OKLab/OKLCH
 * Coefficients are Björn Ottosson's published OKLab matrices.
 * ------------------------------------------------------------------ */

const srgbToLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function hexToRgb(hex) {
  let h = String(hex).replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
}

/** @returns {{L: number, C: number, H: number}} L in 0..1, C in 0..~0.4, H in degrees. */
export function toOklch(hex) {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
  let H = (Math.atan2(B, A) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { L, C: Math.hypot(A, B), H };
}

/**
 * OKLCH -> sRGB hex. Out-of-gamut values clamp per channel, which shifts hue; call
 * `capChroma` first.
 */
export function fromOklch(L, C, H) {
  const hr = (H * Math.PI) / 180;
  const A = C * Math.cos(hr);
  const B = C * Math.sin(hr);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.2914855480 * B) ** 3;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
  const toByte = (v) => {
    const clamped = Math.max(0, Math.min(1, linearToSrgb(Math.max(0, Math.min(1, v)))));
    return Math.round(clamped * 255).toString(16).padStart(2, '0');
  };
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}

/** WCAG relative luminance. */
export function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colors. Returns a number like 4.53. */
export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Shift a hex color's lightness and scale its chroma, preserving hue. */
export function shift(hex, dL, chromaScale = 1, dH = 0) {
  const { L, C, H } = toOklch(hex);
  return fromOklch(Math.max(0, Math.min(1, L + dL)), C * chromaScale, H + dH);
}

/* ------------------------------------------------------------------ *
 * Ramp derivation
 * ------------------------------------------------------------------ */

export const STEPS = ['bg', 'paper', 'raised', 'input', 'hairline', 'control', 'strong'];

/**
 * Lightness per step (indexed to STEPS), per tier. Dark must stay monotonic: the surface
 * step is the elevation there.
 */
const L_LADDER = {
  dark: {
    dim: [15.0, 21.5, 26.5, 30.5, 36.0, 45.0, 57.0],
    mid: [18.5, 25.5, 31.0, 35.5, 41.5, 50.5, 62.0],
    bright: [23.0, 30.5, 36.5, 41.0, 47.0, 56.0, 67.0],
  },
  /**
   * Not dark's ladder inverted: only `paper` climbs toward white; what a card contains
   * (`raised`, `input`) sits below it. `raised` = paper − 2.8 and `input` = paper − 5.6, so
   * a nested panel clears ~2.5 against both the card and its fields. Move one, re-check both.
   */
  light: {
    dim: [92.2, 96.8, 94.0, 91.2, 88.0, 78.5, 53.0],
    mid: [94.4, 98.4, 95.6, 92.8, 90.0, 80.5, 55.0],
    bright: [95.6, 99.3, 96.5, 93.7, 91.5, 82.0, 57.0],
  },
};

/**
 * Nominal chroma per step, before `character`. The chroma ceiling: sRGB holds almost no
 * chroma near white, so light's surfaces can't carry family identity at any `character`;
 * it lives in `control`, `strong` and the command bar. Measure family separation there,
 * never on `paper`.
 */
const C_PROFILE = {
  dark: [0.026, 0.032, 0.036, 0.034, 0.055, 0.080, 0.075],
  light: [0.017, 0.022, 0.016, 0.024, 0.046, 0.085, 0.080],
};

/**
 * Hue rotation per step, in degrees off the spine: inputs cool to read recessed, the rest
 * slightly warm. Keep it small, or it reads as a second colour.
 */
const HUE_ROTATION = {
  bg: 0, paper: 0, raised: 2, input: -8, hairline: 1, control: 3, strong: 4,
};

/** Whether this L/C/H survives conversion to sRGB without any channel clipping. */
function inGamut(L, C, H) {
  const hr = (H * Math.PI) / 180;
  const A = C * Math.cos(hr);
  const B = C * Math.sin(hr);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.2914855480 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ].map(linearToSrgb).every((v) => v >= -0.0005 && v <= 1.0005);
}

/** Largest in-gamut chroma at this L and H, by bisection. Keeps the hue; clipping doesn't. */
export function capChroma(L, C, H) {
  if (inGamut(L, C, H)) return C;
  let lo = 0;
  let hi = C;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (inGamut(L, mid, H)) lo = mid; else hi = mid;
  }
  return lo;
}

/**
 * Taper chroma toward the lightness extremes, with a 55% floor. The floor is load-bearing:
 * light's surfaces all sit near the extreme, and a full taper erases family identity.
 */
function chromaTaper(L) {
  const headroom = Math.min(L, 1 - L);
  return 0.55 + 0.45 * Math.min(1, headroom / 0.10);
}

/**
 * Derive one seven-step neutral ramp.
 *
 * @param {'light'|'dark'} mode
 * @param {number} spineHue - the family's identity hue, in degrees
 * @param {number} character - 0 neutral to 1 fully tinted (see tokens.js families)
 * @param {'dim'|'mid'|'bright'} tier
 * @returns {Record<typeof STEPS[number], string>}
 */
export function deriveRamp(mode, spineHue, character, tier) {
  const ladder = L_LADDER[mode][tier];
  const profile = C_PROFILE[mode];
  const ramp = {};
  STEPS.forEach((step, i) => {
    const L = ladder[i] / 100;
    const H = spineHue + HUE_ROTATION[step];
    const C = capChroma(L, profile[i] * character * chromaTaper(L), H);
    ramp[step] = fromOklch(L, C, H);
  });
  return ramp;
}
